import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api-client';
import ImageStudio from '../components/ImageStudio';

interface DialogSummary {
  id: string;
  language_code: string;
  level: string;
  title: string;
  status: 'published' | 'archived' | 'draft';
  quality_score: number;
  line_count: number;
  vocab_count: number;
  scenario_code?: string;
  thumbnail_url?: string | null;
  created_at: string;
  updated_at: string;
}

interface DialogSpeaker {
  index: number;
  id?: string;
  name: string;
  gender: string;
  role?: string;
  age: string;
  timbre: string;
  fonVoiceId?: string | null;
  fallbackUsed?: boolean;
  accent?: string;
  language?: string;
  ethnicity?: string;
  character_id?: string;
  avatarBase?: string | null;
}

interface DialogLine {
  index: number;
  speaker?: string;
  speakerIndex: number;
  text: string;
  romanization?: string | null;
  translation?: Record<string, string>;
  emotion: string;
  audioUrl?: string | null;
  avatarFace?: string | null;
}

interface DialogDetail {
  id: string;
  language_code: string;
  level: string;
  title: string;
  introduction: string;
  status: string;
  quality: { score: number; criteria: Record<string, number> };
  speakers: DialogSpeaker[];
  lines: DialogLine[];
  vocabulary: Array<{ id: string; term: string; pos_hint: string; lemma_hint: string }>;
  notes?: string;
  notes_en?: string;
  notes_translations?: Record<string, string>;
  scenario_code?: string;
  scene_metadata?: { country?: string; ethnicity?: string; scene_description?: string };
  speaker_thumbnails?: Record<string, { url: string; speaker_name: string; uploaded_at: string }>;
  image_prompts?: Record<string, string>;
  pending_images?: Record<string, Array<{ url: string; character_id: string }>>;
  created_at: string;
  updated_at: string;
}

interface DialogStats {
  total: number;
  published: number;
  archived: number;
  draft: number;
  by_language: Record<string, { total: number; published: number; archived: number }>;
  by_level: Record<string, { total: number; published: number; archived: number }>;
}

type StatusFilter = 'all' | 'published' | 'archived' | 'draft';

export default function Dialogs() {
  const [dialogs, setDialogs] = useState<DialogSummary[]>([]);
  const [stats, setStats] = useState<DialogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [langFilter, setLangFilter] = useState('');
  const [availableLangs, setAvailableLangs] = useState<string[]>([]);
  const [levelFilter, setLevelFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedDialog, setSelectedDialog] = useState<DialogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [thumbnailResults, setThumbnailResults] = useState<Array<{ speaker: number; name?: string; url?: string; cached?: boolean; error?: string; sent?: Record<string, string> }> | null>(null);
  const [keyDebug, setKeyDebug] = useState<Record<string, unknown> | null>(null);
  const [candidates, setCandidates] = useState<Record<number, string[]>>({});
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateProgress, setCandidateProgress] = useState('');
  const [selectedAvatars, setSelectedAvatars] = useState<Record<number, string>>({});
  const [emotionImages, setEmotionImages] = useState<Record<string, string>>({});
  const [emotionLoading, setEmotionLoading] = useState(false);
  const [emotionProgress, setEmotionProgress] = useState('');
  const [candidateErrors, setCandidateErrors] = useState<Record<number, string[]>>({});
  const [repairLoading, setRepairLoading] = useState(false);
  const [simplifyLoading, setSimplifyLoading] = useState(false);
  const [thumbnailSetting, setThumbnailSetting] = useState(false);
  const [thumbnailSet, setThumbnailSet] = useState<{ speaker: number; url: string } | null>(null);
  const [emotionDone, setEmotionDone] = useState<string | null>(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillLog, setBackfillLog] = useState<string[]>([]);
  const [uploadingThumbnail, setUploadingThumbnail] = useState<number | null>(null);
  const [uploadLog, setUploadLog] = useState<string | null>(null);
  const [speakerThumbnails, setSpeakerThumbnails] = useState<Record<string, { url: string; speaker_name: string; uploaded_at: string }>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [imagePrompts, setImagePrompts] = useState<Record<number, string>>({});
  const [generatingImage, setGeneratingImage] = useState<number | null>(null);
  const [generatedImages, setGeneratedImages] = useState<Record<number, Array<{ url: string; character_id: string }>>>({});
  const PAGE_SIZE = 50;

  const fetchDialogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (langFilter) params.set('language', langFilter);
      if (levelFilter) params.set('level', levelFilter);
      params.set('offset', String(page * PAGE_SIZE));
      params.set('limit', String(PAGE_SIZE));

      const data = await apiClient<{ dialogs: DialogSummary[]; stats: DialogStats }>(
        `/api/admin/dialogs?${params}`,
      );
      setDialogs(data.dialogs);
      setStats(data.stats);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dialogs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, langFilter, levelFilter, page]);

  useEffect(() => { fetchDialogs(); }, [fetchDialogs]);

  useEffect(() => {
    apiClient<{ languages: string[] }>('/api/admin/dialogs/languages')
      .then(data => { if (data.languages) setAvailableLangs(data.languages.sort()); })
      .catch(() => {});
  }, []);

  const openDialog = async (id: string) => {
    setDetailLoading(true);
    setThumbnailResults(null);
    setThumbnailSet(null);
    setEmotionDone(null);
    setCandidates({});
    setSelectedAvatars({});
    setEmotionImages({});
    setCandidateErrors({});
    setSpeakerThumbnails({});
    setUploadLog(null);
    setImagePrompts({});
    setGeneratedImages({});
    setGeneratingImage(null);
    setLightboxUrl(null);
    try {
      const data = await apiClient<DialogDetail>(`/api/admin/dialogs/${encodeURIComponent(id)}`);
      setSelectedDialog(data);
      if (data.speaker_thumbnails) setSpeakerThumbnails(data.speaker_thumbnails);
      if (data.image_prompts) setImagePrompts(Object.fromEntries(Object.entries(data.image_prompts).map(([k, v]) => [Number(k), v as string])));
      if (data.pending_images) setGeneratedImages(data.pending_images as Record<number, Array<{ url: string; character_id: string }>>);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dialog');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (dialogId: string, newStatus: 'published' | 'archived') => {
    try {
      await apiClient(`/api/admin/dialogs/${encodeURIComponent(dialogId)}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      setSelectedDialog(null);
      fetchDialogs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const generateCandidates = async (dialogId: string, speakerCount: number) => {
    setCandidateLoading(true);
    setCandidates({});
    setSelectedAvatars({});
    setEmotionImages({});
    setCandidateErrors({});
    const all: Record<number, string[]> = {};
    const errs: Record<number, string[]> = {};
    for (let si = 0; si < speakerCount; si++) { all[si] = []; errs[si] = []; }

    for (let round = 0; round < 4; round++) {
      for (let si = 0; si < speakerCount; si++) {
        setCandidateProgress(`Generating candidate ${round + 1}/4 for speaker ${si + 1}...`);
        try {
          const data = await apiClient<{ ok: boolean; results: Array<{ speaker: number; url?: string; error?: string; sent?: Record<string, unknown> }> }>(
            `/api/admin/dialogs/${encodeURIComponent(dialogId)}/generate-thumbnail`,
            {
              method: 'POST',
              body: JSON.stringify({
                speaker: si,
                emotions: ['neutral'],
                skip_cache: true,
              }),
            },
          );
          const res = data.results?.find(r => r.speaker === si);
          if (res?.url) {
            all[si] = [...(all[si] ?? []), res.url];
            setCandidates({ ...all });
          } else if (res?.error) {
            errs[si] = [...(errs[si] ?? []), `Round ${round + 1}: ${res.error}${res.sent ? ` | sent: ${JSON.stringify(res.sent)}` : ''}`];
            setCandidateErrors({ ...errs });
          } else {
            errs[si] = [...(errs[si] ?? []), `Round ${round + 1}: No URL returned`];
            setCandidateErrors({ ...errs });
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          errs[si] = [...(errs[si] ?? []), `Round ${round + 1}: ${msg}`];
          setCandidateErrors({ ...errs });
        }
      }
    }
    setCandidateProgress('');
    setCandidateLoading(false);
  };

  const selectAvatar = (speakerIndex: number, url: string) => {
    setSelectedAvatars(prev => ({ ...prev, [speakerIndex]: url }));
  };

  const repairSpeakers = async (dialogId: string) => {
    setRepairLoading(true);
    try {
      const data = await apiClient<{ ok: boolean; fixed: number }>(`/api/admin/dialogs/${encodeURIComponent(dialogId)}/repair-speakers`, { method: 'POST' });
      if (data.fixed > 0) {
        const refreshed = await apiClient<DialogDetail>(`/api/admin/dialogs/${encodeURIComponent(dialogId)}`);
        setSelectedDialog(refreshed);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to repair speakers');
    } finally {
      setRepairLoading(false);
    }
  };

  const simplifyEmotions = async (dialogId: string) => {
    setSimplifyLoading(true);
    try {
      const data = await apiClient<{ ok: boolean; changed: number }>(`/api/admin/dialogs/${encodeURIComponent(dialogId)}/simplify-emotions`, { method: 'POST' });
      if (data.changed > 0) {
        const refreshed = await apiClient<DialogDetail>(`/api/admin/dialogs/${encodeURIComponent(dialogId)}`);
        setSelectedDialog(refreshed);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to simplify emotions');
    } finally {
      setSimplifyLoading(false);
    }
  };

  const runBackfill = async (language: string) => {
    if (!language) { setError('Select a language filter first'); return; }
    setBackfillRunning(true);
    setBackfillLog([]);
    let total = 0;
    let remaining = true;
    while (remaining) {
      try {
        const data = await apiClient<{ ok: boolean; processed: number; remaining: boolean; results: Array<{ id: string; ok?: boolean; error?: string; emotions_set?: number }> }>(
          '/api/admin/dialogs/backfill',
          { method: 'POST', body: JSON.stringify({ language, batch_size: 1 }) },
        );
        if (data.processed === 0) { remaining = false; break; }
        total += data.processed;
        remaining = data.remaining;
        for (const r of data.results) {
          const msg = r.ok ? `${r.id}: emotions=${r.emotions_set}` : `${r.id}: ERROR ${r.error}`;
          setBackfillLog(prev => [...prev, msg]);
        }
        setBackfillLog(prev => [...prev, `--- ${total} processed so far ---`]);
      } catch (err) {
        setBackfillLog(prev => [...prev, `FATAL: ${err instanceof Error ? err.message : String(err)}`]);
        remaining = false;
      }
    }
    setBackfillLog(prev => [...prev, `DONE: ${total} dialogs backfilled for ${language}`]);
    setBackfillRunning(false);
  };

  const setAsThumbnail = async (dialogId: string, speakerIndex: number, url: string) => {
    setThumbnailSetting(true);
    try {
      await apiClient(`/api/admin/dialogs/${encodeURIComponent(dialogId)}/set-thumbnail`, {
        method: 'POST',
        body: JSON.stringify({ thumbnail_url: url, speaker_index: speakerIndex }),
      });
      setThumbnailSet({ speaker: speakerIndex, url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set thumbnail');
    } finally {
      setThumbnailSetting(false);
    }
  };

  const uploadThumbnail = async (dialogId: string, file: File, speakerIndex: number, speakerName: string) => {
    setUploadingThumbnail(speakerIndex);
    setUploadLog(`Uploading thumbnail for ${speakerName} (speaker ${speakerIndex + 1})…`);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
      const base64 = btoa(binary);

      const data = await apiClient<{ ok: boolean; thumbnail_url: string; speaker_index: number; speaker_name: string; speaker_thumbnails: Record<string, { url: string; speaker_name: string; uploaded_at: string }> }>(
        `/api/admin/dialogs/${encodeURIComponent(dialogId)}/upload-thumbnail`,
        {
          method: 'POST',
          body: JSON.stringify({
            image_base64: base64,
            content_type: file.type || 'image/jpeg',
            speaker_index: speakerIndex,
            speaker_name: speakerName,
          }),
        },
      );
      setSpeakerThumbnails(data.speaker_thumbnails || {});
      setUploadLog(`Saved: ${speakerName} thumbnail → ${data.thumbnail_url.slice(0, 70)}…`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to upload thumbnail';
      setError(msg);
      setUploadLog(`FAILED: ${msg}`);
    } finally {
      setUploadingThumbnail(null);
    }
  };

  const savePendingImages = async (dialogId: string, updated: Record<number, Array<{ url: string; character_id: string }>>) => {
    try {
      await apiClient(`/api/admin/dialogs/${encodeURIComponent(dialogId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ pending_images: updated }),
      });
    } catch (err) {
      console.error('Failed to save pending images:', err);
    }
  };

  const generateFromPrompt = async (dialogId: string, speakerIndex: number, speakerName: string, prompt: string, ethnicity: string, gender: string) => {
    setGeneratingImage(speakerIndex);
    try {
      const ts = Date.now();
      const results: Array<{ url: string; character_id: string }> = [];
      for (let i = 1; i <= 2; i++) {
        const charId = `${dialogId}-sp${speakerIndex}-v${i}-${ts}`;
        const data = await apiClient<{ ok: boolean; url: string; character_id: string }>('/api/admin/ikon/generate', {
          method: 'POST',
          body: JSON.stringify({ character_id: charId, prompt, ethnicity, gender }),
        });
        if (data.ok && data.url) results.push({ url: data.url, character_id: data.character_id });
      }
      const updated = { ...generatedImages, [speakerIndex]: [...(generatedImages[speakerIndex] || []), ...results] };
      setGeneratedImages(updated);
      await savePendingImages(dialogId, updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Image generation failed');
    } finally {
      setGeneratingImage(null);
    }
  };

  const declineImage = async (dialogId: string, speakerIndex: number, url: string) => {
    const updated = {
      ...generatedImages,
      [speakerIndex]: (generatedImages[speakerIndex] || []).filter(img => img.url !== url),
    };
    setGeneratedImages(updated);
    await savePendingImages(dialogId, updated);
  };

  const selectAndSaveImage = async (dialogId: string, speakerIndex: number, speakerName: string, url: string) => {
    setThumbnailSetting(true);
    try {
      const newThumbs = { ...speakerThumbnails, [String(speakerIndex)]: { url, speaker_name: speakerName, uploaded_at: new Date().toISOString() } };
      await apiClient(`/api/admin/dialogs/${encodeURIComponent(dialogId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ speaker_thumbnails: newThumbs, manually_curated: true }),
      });
      setSpeakerThumbnails(newThumbs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save speaker image');
    } finally {
      setThumbnailSetting(false);
    }
  };

  const setDialogThumbnail = async (dialogId: string, speakerIndex: number, url: string) => {
    setThumbnailSetting(true);
    try {
      await apiClient(`/api/admin/dialogs/${encodeURIComponent(dialogId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ thumbnail_url: url }),
      });
      setThumbnailSet({ speaker: speakerIndex, url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set thumbnail');
    } finally {
      setThumbnailSetting(false);
    }
  };

  const generateEmotions = async (dialogId: string, speakers: DialogSpeaker[], lines: DialogLine[]) => {
    setEmotionLoading(true);
    setEmotionImages({});
    setEmotionDone(null);
    const emotionsBySpeaker: Record<number, Set<string>> = {};
    for (const line of lines) {
      const si = line.speakerIndex;
      if (!emotionsBySpeaker[si]) emotionsBySpeaker[si] = new Set();
      emotionsBySpeaker[si].add(line.emotion || 'neutral');
    }

    const newImages: Record<string, string> = {};
    for (const [siStr, emotionSet] of Object.entries(emotionsBySpeaker)) {
      const si = Number(siStr);
      const refUrl = selectedAvatars[si];
      if (!refUrl) continue;
      const emotions = Array.from(emotionSet);
      for (let i = 0; i < emotions.length; i++) {
        const emotion = emotions[i];
        setEmotionProgress(`Speaker ${si + 1}: ${emotion} (${i + 1}/${emotions.length})`);
        try {
          const data = await apiClient<{ ok: boolean; results: Array<{ speaker: number; emotion: string; url?: string; error?: string }> }>(
            `/api/admin/dialogs/${encodeURIComponent(dialogId)}/generate-thumbnail`,
            {
              method: 'POST',
              body: JSON.stringify({
                speaker: si,
                emotions: [emotion],
                skip_cache: true,
                reference_image_url: refUrl,
              }),
            },
          );
          const res = data.results?.find(r => r.speaker === si && r.emotion === emotion);
          if (res?.url) {
            const key = `${si}-${emotion}`;
            newImages[key] = res.url;
            setEmotionImages({ ...newImages });
          }
        } catch (err) {
          console.error(`Emotion ${emotion} speaker ${si} failed:`, err);
        }
      }
    }
    const totalGenerated = Object.keys(newImages).length;
    const speakerCount = Object.keys(emotionsBySpeaker).filter(si => selectedAvatars[Number(si)]).length;
    setEmotionDone(`Done: ${totalGenerated} emotion images generated for ${speakerCount} speaker${speakerCount !== 1 ? 's' : ''}`);
    setEmotionProgress('');
    setEmotionLoading(false);
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      published: 'bg-emerald-900 text-emerald-300',
      archived: 'bg-red-900 text-red-300',
      draft: 'bg-amber-900 text-amber-300',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-zinc-700 text-zinc-300'}`}>
        {status}
      </span>
    );
  };

  const qualityBadge = (score: number) => {
    const color = score >= 8 ? 'text-emerald-400' : score >= 7 ? 'text-amber-400' : 'text-red-400';
    return <span className={`font-mono text-sm ${color}`}>{score.toFixed(1)}</span>;
  };

  const SPEAKER_COLORS = [
    { bg: 'bg-teal-900', text: 'text-teal-300', label: 'text-teal-400' },
    { bg: 'bg-amber-900', text: 'text-amber-300', label: 'text-amber-400' },
    { bg: 'bg-violet-900', text: 'text-violet-300', label: 'text-violet-400' },
    { bg: 'bg-rose-900', text: 'text-rose-300', label: 'text-rose-400' },
  ];

  if (selectedDialog) {
    const d = selectedDialog;
    const speakerMap = new Map(d.speakers?.map(s => [s.index, s]) ?? []);

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => { setSelectedDialog(null); setLightboxUrl(null); }} className="text-zinc-400 hover:text-zinc-200">Back</button>
            <h1 className="text-2xl font-bold text-zinc-100">{d.title || 'Untitled'}</h1>
            {statusBadge(d.status)}
            {qualityBadge(d.quality?.score ?? 0)}
          </div>
          <div className="flex gap-2">
            {error && (
              <div className="bg-red-900/50 border border-red-700 text-red-300 px-3 py-2 rounded text-xs cursor-pointer" onClick={() => setError(null)}>
                {error} (click to dismiss)
              </div>
            )}
            <button
              onClick={() => repairSpeakers(d.id)}
              disabled={repairLoading}
              className="px-3 py-1.5 rounded bg-blue-800 text-blue-200 hover:bg-blue-700 disabled:opacity-40 text-xs"
            >
              {repairLoading ? 'Repairing...' : 'Repair Speakers'}
            </button>
            <button
              onClick={() => simplifyEmotions(d.id)}
              disabled={simplifyLoading}
              className="px-3 py-1.5 rounded bg-orange-800 text-orange-200 hover:bg-orange-700 disabled:opacity-40 text-xs"
            >
              {simplifyLoading ? 'Simplifying...' : 'Simplify Emotions'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
            <div className="text-zinc-500 text-xs">Language</div>
            <div className="text-zinc-100 font-mono text-base md:text-lg">{d.language_code}</div>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
            <div className="text-zinc-500 text-xs">CEFR Level</div>
            <div className="text-zinc-100 font-mono text-base md:text-lg">{d.level}</div>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
            <div className="text-zinc-500 text-xs">Lines</div>
            <div className="text-zinc-100 font-mono text-base md:text-lg">{d.lines?.length ?? 0}</div>
          </div>
          {d.scenario_code && (
            <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
              <div className="text-zinc-500 text-xs">Scenario</div>
              <div className="text-zinc-100 font-mono text-sm truncate">{d.scenario_code}</div>
            </div>
          )}
        </div>
        {d.scene_metadata && (
          <div className="flex flex-wrap gap-3 text-xs">
            {d.scene_metadata.country && (
              <span className="bg-zinc-800 rounded px-2 py-1 text-zinc-400">
                {d.scene_metadata.country}
              </span>
            )}
            {d.scene_metadata.scene_description && (
              <span className="bg-zinc-800 rounded px-2 py-1 text-zinc-400">
                {d.scene_metadata.scene_description}
              </span>
            )}
          </div>
        )}

        {d.introduction && (
          <div className="bg-zinc-800 rounded-lg p-4">
            <div className="text-zinc-500 text-xs mb-1">Introduction</div>
            <p className="text-zinc-300">{d.introduction}</p>
          </div>
        )}

        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
            <div className="text-zinc-500 text-xs">Speakers &amp; Avatars</div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => generateCandidates(d.id, d.speakers?.length ?? 0)}
                disabled={candidateLoading || emotionLoading}
                className="px-3 py-1.5 rounded bg-violet-800 text-violet-200 hover:bg-violet-700 disabled:opacity-40 text-xs"
              >
                {candidateLoading ? candidateProgress || 'Generating...' : 'Generate Thumbnails'}
              </button>
              {Object.keys(selectedAvatars).length === (d.speakers?.length ?? 0) && (d.speakers?.length ?? 0) > 0 && (
                <button
                  onClick={() => generateEmotions(d.id, d.speakers ?? [], d.lines ?? [])}
                  disabled={emotionLoading}
                  className="px-3 py-1.5 rounded bg-teal-800 text-teal-200 hover:bg-teal-700 disabled:opacity-40 text-xs"
                >
                  {emotionLoading ? emotionProgress || 'Generating...' : 'Generate Emotions'}
                </button>
              )}
            </div>
          </div>

          {(d.speakers ?? []).map(s => {
            const speakerEmotions = [...new Set((d.lines ?? []).filter(l => l.speakerIndex === s.index).map(l => l.emotion || 'neutral'))].sort();
            return (
              <div key={s.index} className="mb-4">
                <div className="bg-zinc-700 rounded px-3 py-3 text-sm flex gap-3">
                  {(selectedAvatars[s.index] || s.avatarBase) && (
                    <img
                      src={selectedAvatars[s.index] || s.avatarBase || ''}
                      alt={`${s.name} avatar`}
                      className="w-16 h-16 rounded-lg object-cover shrink-0"
                    />
                  )}
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-100 font-medium">{s.name}</span>
                      {s.role && <span className="text-zinc-400 text-xs bg-zinc-600 rounded px-1.5 py-0.5">{s.role}</span>}
                      <span className="text-zinc-500">{s.gender} {s.age} {s.timbre}</span>
                    </div>
                    {s.ethnicity && <span className="text-zinc-500 text-xs">{s.ethnicity}</span>}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {speakerEmotions.map(e => (
                        <span key={e} className="bg-zinc-600 text-zinc-300 rounded px-1.5 py-0.5 text-[10px]">{e}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {(candidates[s.index]?.length ?? 0) > 0 && (
                  <div className="mt-2">
                    <div className="text-zinc-500 text-xs mb-1">Pick avatar for {s.name}:</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {(candidates[s.index] ?? []).map((url, ci) => (
                        <button
                          key={ci}
                          onClick={() => selectAvatar(s.index, url)}
                          className={`rounded-lg overflow-hidden border-2 transition-all ${
                            selectedAvatars[s.index] === url
                              ? 'border-teal-400 ring-2 ring-teal-400/30'
                              : 'border-transparent hover:border-zinc-500'
                          }`}
                        >
                          <img src={url} alt={`Candidate ${ci + 1}`} className="w-full aspect-[2/3] object-cover" />
                          <div className="bg-zinc-900 px-2 py-1 text-xs text-zinc-400 text-center">
                            #{ci + 1} {selectedAvatars[s.index] === url && '✓ Selected'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {selectedAvatars[s.index] && (
                    <button
                      onClick={() => setAsThumbnail(d.id, s.index, selectedAvatars[s.index]!)}
                      disabled={thumbnailSetting}
                      className={`px-3 py-1.5 rounded text-xs font-medium ${
                        thumbnailSet?.speaker === s.index
                          ? 'bg-emerald-800 text-emerald-200'
                          : 'bg-indigo-800 text-indigo-200 hover:bg-indigo-700'
                      } disabled:opacity-40`}
                    >
                      {thumbnailSetting ? 'Setting...' : thumbnailSet?.speaker === s.index ? 'Dialog Thumbnail (set)' : 'Set as Dialog Thumbnail'}
                    </button>
                  )}
                  <label className={`px-3 py-1.5 rounded text-xs font-medium bg-cyan-800 text-cyan-200 hover:bg-cyan-700 cursor-pointer transition-colors ${uploadingThumbnail === s.index ? 'opacity-40 pointer-events-none' : ''}`}>
                    {uploadingThumbnail === s.index ? 'Uploading…' : `Upload Thumbnail for ${s.name}`}
                    <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadThumbnail(d.id, f, s.index, s.name); e.target.value = ''; }} />
                  </label>
                  {(() => { const th = speakerThumbnails[String(s.index)]; return th ? (
                    <div className="flex items-center gap-3">
                      <img src={th.url} alt={`${s.name} thumbnail`} className="w-32 h-40 rounded-lg object-cover border-2 border-emerald-700 hover:border-emerald-400 transition-colors cursor-pointer" onClick={e => { e.stopPropagation(); setLightboxUrl(th.url); }} title="Click to enlarge" />
                      <button
                        onClick={() => setAsThumbnail(d.id, s.index, th.url)}
                        disabled={thumbnailSetting}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          thumbnailSet?.speaker === s.index && thumbnailSet.url === th.url
                            ? 'bg-emerald-800 text-emerald-200'
                            : 'bg-amber-800 text-amber-200 hover:bg-amber-700'
                        } disabled:opacity-40`}
                      >
                        {thumbnailSet?.speaker === s.index && thumbnailSet.url === th.url ? 'Primary' : 'Set as primary'}
                      </button>
                    </div>
                  ) : null; })()}
                </div>

                <div className="mt-3 bg-zinc-900 rounded-lg p-3">
                  <div className="text-zinc-500 text-xs mb-2">Image Prompt for {s.name}</div>
                  <textarea
                    className="w-full bg-zinc-800 border border-zinc-700 rounded text-zinc-200 text-sm p-2 focus:outline-none focus:border-teal-500"
                    rows={6}
                    placeholder={`Enter a detailed prompt for ${s.name}'s portrait...`}
                    value={imagePrompts[s.index] ?? ''}
                    onChange={e => setImagePrompts(prev => ({ ...prev, [s.index]: e.target.value }))}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => generateFromPrompt(d.id, s.index, s.name, imagePrompts[s.index] || '', d.scene_metadata?.ethnicity || 'korean', s.gender)}
                      disabled={generatingImage === s.index || !imagePrompts[s.index]?.trim()}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-teal-700 text-teal-100 hover:bg-teal-600 disabled:opacity-40 transition-colors"
                    >
                      {generatingImage === s.index ? 'Generating 2 images...' : 'Generate Images'}
                    </button>
                    <button
                      onClick={async () => {
                        const allPrompts = { ...d.image_prompts, [String(s.index)]: imagePrompts[s.index] || '' };
                        await apiClient(`/api/admin/dialogs/${encodeURIComponent(d.id)}`, { method: 'PATCH', body: JSON.stringify({ image_prompts: allPrompts }) });
                        d.image_prompts = allPrompts;
                      }}
                      disabled={!imagePrompts[s.index]?.trim()}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-40 transition-colors"
                    >
                      Save Prompt
                    </button>
                  </div>
                  {(generatedImages[s.index] || []).length > 0 && (
                    <div className="mt-3">
                      <div className="text-zinc-500 text-xs mb-2">Generated Images (click to enlarge, select to use)</div>
                      <div className="flex flex-wrap gap-3">
                        {(generatedImages[s.index] || []).map((img, gi) => (
                          <div key={img.character_id} className="relative">
                            <img
                              src={img.url}
                              alt={`Generated ${gi + 1}`}
                              className="w-36 h-48 rounded-lg object-cover border-2 border-zinc-600 hover:border-teal-400 cursor-pointer transition-colors"
                              onClick={e => { e.stopPropagation(); setLightboxUrl(img.url); }}
                            />
                            <div className="flex flex-col gap-1 mt-1">
                              <div className="flex gap-1">
                                <button
                                  onClick={() => selectAndSaveImage(d.id, s.index, s.name, img.url)}
                                  disabled={thumbnailSetting}
                                  className={`flex-1 px-2 py-1 rounded text-xs font-medium ${speakerThumbnails[String(s.index)]?.url === img.url ? 'bg-emerald-600 text-white ring-2 ring-emerald-400' : 'bg-emerald-800 text-emerald-200 hover:bg-emerald-700'} disabled:opacity-40`}
                                >
                                  {thumbnailSetting ? 'Saving...' : speakerThumbnails[String(s.index)]?.url === img.url ? 'Selected' : 'Use'}
                                </button>
                                <button
                                  onClick={() => declineImage(d.id, s.index, img.url)}
                                  className="px-2 py-1 rounded text-xs bg-red-900 text-red-300 hover:bg-red-800"
                                >
                                  Decline
                                </button>
                              </div>
                              {speakerThumbnails[String(s.index)]?.url === img.url && (
                                <button
                                  onClick={() => setDialogThumbnail(d.id, s.index, img.url)}
                                  disabled={thumbnailSetting}
                                  className={`w-full px-2 py-1 rounded text-xs ${thumbnailSet?.url === img.url ? 'bg-amber-700 text-amber-100' : 'bg-amber-800 text-amber-200 hover:bg-amber-700'} disabled:opacity-40`}
                                >
                                  {thumbnailSet?.url === img.url ? 'Dialog Thumbnail (set)' : 'Set as Dialog Thumbnail'}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {Object.entries(emotionImages).filter(([k]) => k.startsWith(`${s.index}-`)).length > 0 && (
                  <div className="mt-3">
                    <div className="text-zinc-500 text-xs mb-1">Emotion variants for {s.name}:</div>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                      {Object.entries(emotionImages)
                        .filter(([k]) => k.startsWith(`${s.index}-`))
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([key, url]) => {
                          const emotion = key.split('-').slice(1).join('-');
                          return (
                            <div key={key} className="rounded-lg overflow-hidden border border-zinc-600">
                              <img src={url} alt={emotion} className="w-full aspect-[2/3] object-cover" />
                              <div className="bg-zinc-900 px-2 py-1 text-xs text-zinc-400 text-center truncate">{emotion}</div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {(candidateErrors[s.index]?.length ?? 0) > 0 && (
                  <div className="mt-2 bg-red-900/30 border border-red-800 rounded p-2">
                    <div className="text-red-400 text-xs font-medium mb-1">Thumbnail errors for {s.name}:</div>
                    {(candidateErrors[s.index] ?? []).map((err, ei) => (
                      <div key={ei} className="text-red-300 text-xs font-mono break-all">{err}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {emotionDone && (
            <div className="mt-3 bg-emerald-900/30 border border-emerald-700 rounded px-3 py-2 text-emerald-300 text-sm">
              {emotionDone}
            </div>
          )}

          {uploadLog && (
            <div className={`mt-3 rounded px-3 py-2 text-sm font-mono ${
              uploadLog.startsWith('FAILED') ? 'bg-red-900/30 border border-red-800 text-red-300' :
              uploadLog.startsWith('Saved') ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-300' :
              'bg-zinc-900/50 border border-zinc-700 text-zinc-400'
            }`}>
              {uploadLog}
            </div>
          )}
        </div>

        <ImageStudio
          product="dialogs"
          productKey={d.id}
          slots={[{ value: 'thumbnail', label: 'Thumbnail' }]}
          onSelect={(url) => {
            setThumbnailSet({ speaker: 0, url });
          }}
        />

        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs mb-3">
            Dialog Lines
            <span className="ml-2 text-zinc-600">
              ({(d.lines ?? []).filter(l => l.audioUrl).length}/{(d.lines ?? []).length} with audio)
            </span>
          </div>
          <div className="space-y-3">
            {(d.lines ?? []).map((line, i) => {
              const speaker = speakerMap.get(line.speakerIndex);
              const emotionKey = `${line.speakerIndex}-${line.emotion || 'neutral'}`;
              const spThumb = speakerThumbnails[String(line.speakerIndex)]?.url;
              const emotionImg = emotionImages[emotionKey] || selectedAvatars[line.speakerIndex] || spThumb || speaker?.avatarBase;
              const sc = SPEAKER_COLORS[line.speakerIndex % SPEAKER_COLORS.length]!;
              return (
                <div key={i} className="flex gap-2 md:gap-3">
                  <div className="w-12 shrink-0 flex flex-col items-center gap-1">
                    {emotionImg ? (
                      <img src={emotionImg} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${sc.bg} ${sc.text}`}>
                        {speaker?.name?.charAt(0) ?? '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-medium ${sc.label}`}>
                        {speaker?.name ?? `Speaker ${line.speakerIndex}`}
                      </span>
                      {line.emotion && line.emotion !== 'unset' && (
                        <span className="text-zinc-600 text-[10px] bg-zinc-700 rounded px-1.5 py-0.5">{line.emotion}</span>
                      )}
                      {line.audioUrl ? (
                        <span className="text-emerald-500 text-xs" title={line.audioUrl}>&#9834;</span>
                      ) : (
                        <span className="text-zinc-700 text-xs" title="No audio">&#9834;</span>
                      )}
                    </div>
                    <div className="text-zinc-200 text-sm">{line.text}</div>
                    {line.romanization && (
                      <div className="text-zinc-500 text-xs mt-0.5 italic">{line.romanization}</div>
                    )}
                    {line.translation?.en && (
                      <div className="text-zinc-600 text-xs mt-0.5">{line.translation.en}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {d.vocabulary && d.vocabulary.length > 0 && (
          <div className="bg-zinc-800 rounded-lg p-4">
            <div className="text-zinc-500 text-xs mb-2">Vocabulary ({d.vocabulary.length} terms)</div>
            <div className="grid grid-cols-2 gap-1">
              {d.vocabulary.map((v, i) => (
                <div key={i} className="text-sm">
                  <span className="text-zinc-200">{v.term}</span>
                  {v.pos_hint && <span className="text-zinc-600 ml-1 text-xs">{v.pos_hint}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {(d.notes || d.notes_en || d.notes_translations) && (
          <div className="bg-zinc-800 rounded-lg p-4">
            <div className="text-zinc-500 text-xs mb-2">Notes</div>
            {(d.notes_en || d.notes) && (
              <div className="mb-2">
                <span className="text-zinc-500 text-xs font-mono mr-2">en</span>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap inline">{d.notes_en || d.notes}</p>
              </div>
            )}
            {d.notes_translations && Object.entries(d.notes_translations)
              .filter(([lang]) => lang !== 'en')
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([lang, text]) => (
                <div key={lang} className="mb-2">
                  <span className="text-zinc-500 text-xs font-mono mr-2">{lang}</span>
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap inline">{text}</p>
                </div>
              ))
            }
          </div>
        )}

        <div className="flex gap-3">
          {d.status !== 'published' && (
            <button onClick={() => handleStatusChange(d.id, 'published')} className="px-4 py-2 rounded bg-emerald-800 text-emerald-200 hover:bg-emerald-700">Publish</button>
          )}
          {d.status !== 'archived' && (
            <button onClick={() => handleStatusChange(d.id, 'archived')} className="px-4 py-2 rounded bg-red-800 text-red-200 hover:bg-red-700">Archive</button>
          )}
        </div>
        {lightboxUrl && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8 cursor-pointer" onClick={() => setLightboxUrl(null)} onKeyDown={e => { if (e.key === 'Escape') setLightboxUrl(null); }} tabIndex={0} ref={el => el?.focus()}>
            <img src={lightboxUrl} alt="Full-size preview" className="max-w-full max-h-full rounded-lg shadow-2xl cursor-default" onClick={e => e.stopPropagation()} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-100">Dialog Management</h1>
        <p className="text-zinc-400 mt-1">Manage dialog publishing, quality, and mailing status</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
            <div className="text-zinc-400 text-sm">Total</div>
            <div className="text-xl md:text-2xl font-bold text-zinc-100">{stats.total.toLocaleString()}</div>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
            <div className="text-zinc-400 text-sm">Published</div>
            <div className="text-xl md:text-2xl font-bold text-emerald-400">{stats.published.toLocaleString()}</div>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
            <div className="text-zinc-400 text-sm">Archived</div>
            <div className="text-xl md:text-2xl font-bold text-red-400">{stats.archived.toLocaleString()}</div>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 md:p-4">
            <div className="text-zinc-400 text-sm">Draft</div>
            <div className="text-xl md:text-2xl font-bold text-amber-400">{stats.draft.toLocaleString()}</div>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-800 rounded-lg p-4">
            <h3 className="text-zinc-300 font-medium mb-2">By CEFR Level</h3>
            <div className="space-y-1">
              {Object.entries(stats.by_level).sort().map(([level, data]) => (
                <div key={level} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400 font-mono">{level}</span>
                  <div className="flex gap-3">
                    <span className="text-emerald-400">{data.published}</span>
                    <span className="text-red-400">{data.archived}</span>
                    <span className="text-zinc-500">{data.total}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-zinc-800 rounded-lg p-4">
            <h3 className="text-zinc-300 font-medium mb-2">Top Languages</h3>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {Object.entries(stats.by_language)
                .sort((a, b) => b[1].total - a[1].total)
                .slice(0, 15)
                .map(([lang, data]) => (
                  <div key={lang} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400 font-mono">{lang}</span>
                    <div className="flex gap-3">
                      <span className="text-emerald-400">{data.published}</span>
                      <span className="text-red-400">{data.archived}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value as StatusFilter); setPage(0); }}
          className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700"
        >
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
          <option value="draft">Draft</option>
        </select>

        <select
          value={langFilter}
          onChange={e => { setLangFilter(e.target.value); setPage(0); }}
          className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 w-full sm:w-48"
        >
          <option value="">All languages</option>
          {availableLangs.map(l => <option key={l} value={l}>{l}</option>)}
        </select>

        <select
          value={levelFilter}
          onChange={e => { setLevelFilter(e.target.value); setPage(0); }}
          className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700"
        >
          <option value="">All levels</option>
          <option value="A1">A1</option>
          <option value="A2">A2</option>
          <option value="B1">B1</option>
          <option value="B2">B2</option>
          <option value="C1">C1</option>
          <option value="C2">C2</option>
        </select>

      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-zinc-400">Loading dialogs...</div>
      ) : (
        <>
          <div className="bg-zinc-800 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[750px]">
              <thead>
                <tr className="border-b border-zinc-700 text-zinc-400 text-left">
                  <th className="px-4 py-3 w-10"></th>
                  <th className="px-4 py-3">Language</th>
                  <th className="px-4 py-3">Level</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Scenario</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Quality</th>
                  <th className="px-4 py-3">Lines</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-700/50">
                {dialogs.map(d => (
                  <tr key={d.id} className="hover:bg-zinc-700/30 cursor-pointer" onClick={() => openDialog(d.id)}>
                    <td className="px-4 py-2">
                      {d.thumbnail_url ? (
                        <img src={d.thumbnail_url} alt="" className="w-12 h-12 min-w-[48px] rounded object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded bg-zinc-700" />
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-zinc-300">{d.language_code}</td>
                    <td className="px-4 py-2 font-mono text-zinc-300">{d.level}</td>
                    <td className="px-4 py-2 text-zinc-200 max-w-xs truncate">{d.title}</td>
                    <td className="px-4 py-2 text-zinc-500 text-xs max-w-[120px] truncate">{d.scenario_code}</td>
                    <td className="px-4 py-2">{statusBadge(d.status)}</td>
                    <td className="px-4 py-2">{qualityBadge(d.quality_score)}</td>
                    <td className="px-4 py-2 text-zinc-400">{d.line_count}</td>
                    <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-2">
                        {d.status !== 'published' && (
                          <button
                            onClick={() => handleStatusChange(d.id, 'published')}
                            className="text-xs px-2 py-1 rounded bg-emerald-800 text-emerald-200 hover:bg-emerald-700"
                          >
                            Publish
                          </button>
                        )}
                        {d.status !== 'archived' && (
                          <button
                            onClick={() => handleStatusChange(d.id, 'archived')}
                            className="text-xs px-2 py-1 rounded bg-red-800 text-red-200 hover:bg-red-700"
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {detailLoading && <div className="text-zinc-400">Loading dialog details...</div>}

          <div className="flex justify-between items-center">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-4 py-2 bg-zinc-700 text-zinc-200 rounded disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-zinc-400 text-sm">Page {page + 1}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={dialogs.length < PAGE_SIZE}
              className="px-4 py-2 bg-zinc-700 text-zinc-200 rounded disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </>
      )}
      {lightboxUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8" onClick={() => setLightboxUrl(null)} onKeyDown={e => { if (e.key === 'Escape') setLightboxUrl(null); }} tabIndex={0} ref={el => el?.focus()}>
          <img src={lightboxUrl} alt="Full-size preview" className="max-w-full max-h-full rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
